// src/context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getTheme } from '../theme/colors';

const THEME_STORAGE_KEY = 'naim_web_theme_mode';

const ThemeContext = createContext(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }) {
  const [themeMode, setThemeMode] = useState('light'); // Default to light (mint green)
  const [mounted, setMounted] = useState(false);

  // Load saved theme on mount
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme && ['light', 'forest'].includes(savedTheme)) {
        setThemeMode(savedTheme);
      }
    } catch (error) {
      console.warn('Failed to load saved theme:', error);
    }
    setMounted(true);
  }, []);

  // Get current theme data
  const theme = useMemo(() => getTheme(themeMode), [themeMode]);

  // Set theme function
  const setTheme = useCallback((mode) => {
    if (!['light', 'forest'].includes(mode)) return;

    setThemeMode(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
    }
  }, []);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    const nextTheme = themeMode === 'light' ? 'forest' : 'light';
    setTheme(nextTheme);
  }, [themeMode, setTheme]);

  // Memoize context value
  const isForest = themeMode === 'forest'; // Computed for convenience
  const value = useMemo(() => ({
    themeMode,
    theme,
    isForest, // Convenience: true when themeMode === 'forest'
    setTheme,
    toggleTheme,
    mounted,
  }), [themeMode, theme, isForest, setTheme, toggleTheme, mounted]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
